import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  LoadingSpinner,
  getCountryByLabel,
  filterAllowedCountries,
} from '@rabbit/elements/shared-components';
import { useContext, useEffect, useRef, useState } from 'react';
import { SelectAddressShape } from '@rabbit/elements/shared-types';
import { NewClaimModalSteps } from '../../ModalNewClaim/ModalNewClaim';
import { t } from 'i18next';
import {
  CreateNewConsumerForm_FieldNames,
  CreateNewConsumerForm_FormValues,
  ModalNewRegistration_ConfigShape,
} from '../../ModalNewRegistration/tenant-config/types.ts';
import type { Address } from '@rabbit/data/types';
import { AppContext } from '@rabbit/app-context';
import { CORPORATE } from '@rabbit/sage/utils/consts.ts';
import { CNCF_ADDRESS_VALIDATION_SCHEMA } from '../../ModalNewRegistration/tenant-config/common-generic-flow.tsx';

async function getFormConfig(tenantLink: string, type: string | null) {
  const fileName = tenantLink.toLowerCase();
  try {
    if (type && type === CORPORATE) {
      return await import(
        `../../ModalNewRegistration/tenant-config/business-generic-flow.tsx`
      );
    } else {
      return await import(
        `../../ModalNewRegistration/tenant-config/${fileName}.tsx`
      );
    }
  } catch (e) {
    console.warn(
      `File ${`../../ModalNewRegistration/tenant-config/${fileName}.tsx`} not found`
    );
    return await import(
      '../../ModalNewRegistration/tenant-config/common-generic-flow.tsx'
    );
  }
}

export interface CreateNewConsumerFormProps {
  handleClose: () => void;
  onChange: (step: NewClaimModalSteps, data: any) => void;
  data: any;
  isLoading?: boolean;
}

export function CreateNewConsumerForm(props: CreateNewConsumerFormProps) {
  const { onChange, handleClose, isLoading: externalLoading } = props;
  const tenantLink = t('tenantLink');
  const { tenantInfo, config } = useContext(AppContext);
  const [formConfig, setFormConfig] =
    useState<ModalNewRegistration_ConfigShape | null>(null);

  const [type, setType] = useState<string | null>(props.data?.type ?? '');

  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const isSubmitting =
    externalLoading !== undefined ? externalLoading : internalSubmitting;
  const [selectedAddress, setSelectedAddress] =
    useState<SelectAddressShape | null>({} as SelectAddressShape);

  // Build allowed countries list (default to LIST_COUNTRIES)
  const allowedCountries = filterAllowedCountries(
    (tenantInfo as any)?.allowedCountries as string[] | undefined
  );

  const formikRef = useRef(null) as any; //TODO

  // Accept both legacy SelectAddress payload fields and internal Address shape
  type AddressInput =
    | (Partial<Address> & {
        address_line_1?: string;
        address_line_2?: string;
      })
    | null
    | undefined;

  type FormValuesWithLegacy = Omit<
    CreateNewConsumerForm_FormValues,
    'consumer_address'
  > & {
    consumer_address?: AddressInput;
    consumer_telephone_e164?: string;
  };

  const onSubmit = (values: FormValuesWithLegacy) => {
    setInternalSubmitting(true);
    const isAddressRequired = config?.CUSTOMER?.REG_ADDRESS_MANDATORY;
    // NOTE: We support two address naming conventions from different inputs:
    // - address_line_1 / address_line_2 (SelectAddress payloads)
    // - line1 / line2 (internal Address shape)
    // We check both for presence and normalize to line1/line2 before persisting.
    const isEmptyAddress = (addr: AddressInput) => {
      if (!addr) return true;
      const fields = [
        addr.address_line_1,
        addr.address_line_2,
        addr.line1,
        addr.line2,
        addr.town,
        addr.state,
        addr.postcode,
        addr.country,
      ];
      return !fields.some((v) =>
        typeof v === 'string' ? v.trim().length > 0 : Boolean(v)
      );
    };
    const cleanedValues: FormValuesWithLegacy = { ...values };
    // Normalize to line1/line2 and drop legacy keys before persisting
    if (cleanedValues.consumer_address) {
      const a = cleanedValues.consumer_address as NonNullable<AddressInput> & {
        [key: string]: unknown;
      };
      if (!a.line1 && a.address_line_1) a.line1 = a.address_line_1;
      if (!a.line2 && a.address_line_2) a.line2 = a.address_line_2;
      if ('address_line_1' in a) delete a.address_line_1;
      if ('address_line_2' in a) delete a.address_line_2;
    }
    if (!isAddressRequired && isEmptyAddress(cleanedValues.consumer_address)) {
      delete (cleanedValues as { consumer_address?: AddressInput })
        .consumer_address;
    }
    const data = {
      previewData: {
        newUser: cleanedValues,
      },
      ...cleanedValues,
      consumer_telephone:
        cleanedValues.consumer_telephone_e164 ??
        cleanedValues.consumer_telephone,
    };

    // Include promotional email opt-in ONLY when field is shown in config
    if (
      config?.CUSTOMER?.MARKETING_SIGNUP_INFO?.SHOW_PROMOTIONAL_EMAIL_FIELD &&
      values.consumer_promotional_email_opt_in !== undefined
    ) {
      (data as any).consumer_promotional_email_opt_in =
        values.consumer_promotional_email_opt_in;
    }

    onChange('add-product', data);
  };

  useEffect(() => {
    getFormConfig(tenantLink, type).then((module) => {
      setFormConfig(module.ModalNewRegistration_Config);
    });
  }, []);

  useEffect(() => {
    if (!selectedAddress) {
      formikRef.current.resetForm();
      return;
    }
    if (formikRef && selectedAddress?.postal_code) {
      formikRef.current.setFieldValue(
        'consumer_address.line1',
        selectedAddress.line_1
      );
      formikRef.current.setFieldValue(
        'consumer_address.address_line_1',
        selectedAddress.line_1
      );
      formikRef.current.setFieldValue(
        'consumer_address.line2',
        selectedAddress.line_2
      );
      formikRef.current.setFieldValue(
        'consumer_address.address_line_2',
        selectedAddress.line_2
      );
      formikRef.current.setFieldValue(
        'consumer_address.town',
        selectedAddress.locality
      );
      formikRef.current.setFieldValue(
        'consumer_address.state',
        selectedAddress.province_name
      );
      formikRef.current.setFieldValue(
        'consumer_address.postcode',
        selectedAddress.postal_code
      );
      formikRef.current.setFieldValue(
        'consumer_address.country',
        getCountryByLabel(selectedAddress.country_name ?? '')?.value ?? ''
      );
    }
  }, [selectedAddress]);

  if (!formConfig) {
    return <LoadingSpinner size="xs" />;
  }

  const initialValues: FormValuesWithLegacy = formConfig
    .CreateNewConsumerForm(t)
    .generateInitialValues({
      consumer_address: {
        line1: '',
        line2: '',
        town: '',
        state: '',
        postcode: '',
        country: tenantInfo?.country ?? '',
      },
    }) as FormValuesWithLegacy;

  const baseValidationSchema =
    formConfig.CreateNewConsumerForm(t).validationSchema;

  let formValidationSchema = config.CUSTOMER.REG_ADDRESS_MANDATORY
    ? baseValidationSchema.concat(CNCF_ADDRESS_VALIDATION_SCHEMA)
    : baseValidationSchema;

  // Make preferred contact optional when hidden
  if (config.CUSTOMER.HIDE_CONSUMER_PREFERRED_CONTACT) {
    const schemaFields = formValidationSchema.fields;
    formValidationSchema = Yup.object().shape({
      ...schemaFields,
      consumer_preferred_contact: Yup.array().optional(),
    }) as any;
  }

  // Make promotional email opt-in optional (ignore) when field is hidden
  if (!config?.CUSTOMER?.MARKETING_SIGNUP_INFO?.SHOW_PROMOTIONAL_EMAIL_FIELD) {
    const schemaFields = formValidationSchema.fields;
    formValidationSchema = Yup.object().shape({
      ...schemaFields,
      consumer_promotional_email_opt_in: Yup.mixed().optional(),
    }) as any;
  }

  // Make phone number optional when not required
  if (!config?.CUSTOMER?.PHONE_NUMBER_REQUIRED) {
    const schemaFields = formValidationSchema.fields;
    formValidationSchema = Yup.object().shape({
      ...schemaFields,
      consumer_telephone: Yup.string()
        .trim()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .optional(),
    }) as any;
  }

  return (
    <Formik<FormValuesWithLegacy>
      initialValues={initialValues}
      validationSchema={formValidationSchema}
      onSubmit={onSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      innerRef={formikRef}
    >
      {(props) => (
        <Form
          className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2"
          onChange={(event) => {
            const target = event.target as HTMLElement & { name?: string };
            const nameAttr =
              (target as any)?.name ?? target.getAttribute?.('name');
            if (typeof nameAttr === 'string' && nameAttr.length > 0) {
              // Re-validate only the active field while typing if it's already touched
              // or currently shows an error, so the error can disappear immediately
              const isTouched = (props.touched as any)?.[nameAttr];
              const hasError = (props.errors as any)?.[nameAttr] !== undefined;
              if (isTouched || hasError) {
                setTimeout(() => {
                  void props.validateField(nameAttr as any);
                }, 0);
              }
            }
          }}
          onBlurCapture={(event) => {
            const target = event.target as HTMLElement & { name?: string };
            const nameAttr =
              (target as any)?.name ?? target.getAttribute?.('name');
            if (typeof nameAttr === 'string' && nameAttr.length > 0) {
              // Mark only this field as touched and validate only this field
              props.setFieldTouched(nameAttr as any, true, false);
              void props.validateField(nameAttr);
            }
          }}
          onSubmit={async (event) => {
            event.preventDefault();
            const addr = props.values.consumer_address as any;
            if (addr.address_line_1) {
              props.setFieldValue(
                'consumer_address.line1',
                addr.address_line_1
              );
            }
            if (addr.address_line_2) {
              props.setFieldValue(
                'consumer_address.line2',
                addr.address_line_2
              );
            }

            props.setSubmitting(true);
            const errors = await props.validateForm();
            if (Object.keys(errors).length === 0) {
              props.handleSubmit(event);
            } else {
              Object.keys(errors).forEach((fieldName) => {
                props.setFieldTouched(fieldName as any, true, false);
              });
              props.setSubmitting(false);
            }
          }}
        >
          {/* Corporate Fields */}
          {type === CORPORATE && (
            <>
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(CreateNewConsumerForm_FieldNames.business_name)}
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(CreateNewConsumerForm_FieldNames.tax_id)}

              <p className="font-nunito col-span-full mt-2 mb-0 text-base">
                {t('general.primaryContact')}
              </p>
            </>
          )}

          {/* Primary Contact */}
          {formConfig
            .CreateNewConsumerForm(t)
            .generateInput(
              CreateNewConsumerForm_FieldNames.consumer_first_name
            )}
          {formConfig
            .CreateNewConsumerForm(t)
            .generateInput(CreateNewConsumerForm_FieldNames.consumer_last_name)}
          {formConfig
            .CreateNewConsumerForm(t)
            .generateInput(CreateNewConsumerForm_FieldNames.consumer_email)}
          {formConfig
            .CreateNewConsumerForm(t)
            .generateInput(
              CreateNewConsumerForm_FieldNames.consumer_is_commercial,
              { errors: props.errors }
            )}
          {formConfig
            .CreateNewConsumerForm(t)
            .generateInput(CreateNewConsumerForm_FieldNames.consumer_telephone)}
          {!config.CUSTOMER.HIDE_CONSUMER_PREFERRED_CONTACT &&
            formConfig
              .CreateNewConsumerForm(t)
              .generateInput(
                CreateNewConsumerForm_FieldNames.consumer_preferred_contact,
                { errors: props.errors }
              )}

          {config.CUSTOMER.MARKETING_SIGNUP_INFO
            .SHOW_PROMOTIONAL_EMAIL_FIELD && (
            <div className="col-span-full">
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.consumer_promotional_email_opt_in
                )}
            </div>
          )}

          {/* Secondary Contact (Corporate only) */}
          {type === CORPORATE && (
            <>
              <p className="font-nunito text-md col-span-full my-1">
                {t('general.secondaryContact')}
              </p>

              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.secondary_first_name
                )}
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.secondary_last_name
                )}
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.secondary_email
                )}
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.secondary_phone_number
                )}
            </>
          )}

          {/* Address Section */}
          <section className="col-span-full space-y-4">
            <p className="font-nunito my-4 text-base font-medium">
              {type === CORPORATE
                ? t('general.businessAddress')
                : t('general.customerAddress')}
            </p>
            <div className="grid grid-cols-1 gap-4">
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.consumer_address_country,
                  { options: allowedCountries }
                )}
              <div>
                {formConfig
                  .CreateNewConsumerForm(t)
                  .generateInput(
                    CreateNewConsumerForm_FieldNames.consumer_address,
                    {
                      onChange: (address: any) => setSelectedAddress(address),
                      errors: props.errors['consumer_address'],
                      country: props.values?.consumer_address?.country,
                    }
                  )}
              </div>
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.consumer_address_line1
                )}
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.consumer_address_line2
                )}
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.consumer_address_town
                )}
              {formConfig
                .CreateNewConsumerForm(t)
                .generateInput(
                  CreateNewConsumerForm_FieldNames.consumer_address_state
                )}

              <div className="col-span-full">
                {formConfig
                  .CreateNewConsumerForm(t)
                  .generateInput(
                    CreateNewConsumerForm_FieldNames.consumer_address_postcode
                  )}
              </div>
            </div>
          </section>

          {/* Actions */}
          <div className="col-span-full mt-6 flex gap-4">
            <Button kind="primary" type="submit" loading={isSubmitting}>
              {t('Continue')}
            </Button>
            <Button kind="outline_red" type="submit" onClick={handleClose}>
              {t('Cancel')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default CreateNewConsumerForm;
